import React, { useState, useEffect } from "react";
import {
  Input, DatePicker, Space, Select, Switch,
  Button,
} from "antd";
import moment from "moment";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import "./QCFilterBar.css";
import { MANUAL_QC_FLAGS, /* TEST_OVERDUE_FLAGS, */ convertStatusMapToArrayOptions } from "../../../utils/statusConfig";
import {
  buildQcExportRows,
  buildQcExportFileName,
  downloadQcExportExcel,
} from "./utils";

const { RangePicker } = DatePicker;
export const dateFormat = "MM/DD/YYYY";
export const defaultDateRange = () => {
  const startDate = moment().subtract(1, "months").format(dateFormat);
  const endDate = moment().format(dateFormat);
  return [dayjs(startDate, dateFormat), dayjs(endDate, dateFormat)];
};

const QCFilterBar = ({
  setSearch: setParentSearch,
  search: parentSearch,
  dateRange,
  setDateRange,
  testCategories,
  selectedCategory,
  setSelectedCategory,
  fetchQcApi,
  customerList,
  selectedCustomer,
  setSelectedCustomer,
  // selectedAIQC,
  // setSelectedAIQC,
  // selectedSoftwareQC,
  // setSelectedSoftwareQC,
  // selectedMLSoftQC,
  // setSelectedMLSoftQC,
  selectedManualQC,
  setSelectedManualQC,
  assignToList,
  selectedAssignTo,
  setSelectedAssignTo,
  assignToLoading,
  customerLoading,
  // selectedOverdueStatus,
  // setSelectedOverdueStatus,
  // selectedAISoftwareQCDiscrepancy,
  // setSelectedAISoftwareQCDiscrepancy,
  handleRetestOnChange,
  manualQcDefaultValue = 0,
}) => {
  const [search, setSearch] = useState("");
  const onSearchChange = (value) => {
    setSearch(value);
    if (value === "") {
      setParentSearch(value);
    }
  };

  const handleDateChange = (value) => {
    const dateArray = value || defaultDateRange();
    setDateRange(dateArray);
  };

  const handleOnSearch = (value) => {
    if (value === parentSearch) { // If the value hasn't changed and user clicks on the search icon, we trigger the fetch API manually here
      fetchQcApi();
    } else {
      setParentSearch(value);
    }
  };

  useEffect(() => {
    if (parentSearch !== search) {
      setSearch(parentSearch);
    }
  }, [parentSearch]); // eslint-disable-line

  // const handleDiscrepancyChange = (checked) => {
  //   if (checked) {
  //     setSelectedSoftwareQC("");
  //     setSelectedAIQC("");
  //     setSelectedMLSoftQC("");
  //   }
  //   setSelectedAISoftwareQCDiscrepancy(checked);
  // };

  // const handleSoftwareQCChange = (v) => {
  //   if (v !== "") {
  //     setSelectedAISoftwareQCDiscrepancy(false);
  //   }
  //   setSelectedSoftwareQC(v);
  // };

  // const handleAIQCChange = (v) => {
  //   if (v !== "") {
  //     setSelectedAISoftwareQCDiscrepancy(false);
  //   }
  //   setSelectedAIQC(v);
  // };

  // const handleMLSoftQCChange = (v) => {
  //   if (v !== "") {
  //     setSelectedAISoftwareQCDiscrepancy(false);
  //   }
  //   setSelectedMLSoftQC(v);
  // };
  const manualQcOptions = convertStatusMapToArrayOptions(MANUAL_QC_FLAGS, [], true).reduce((arr, item, i, allItems) => {
    arr.push(item);
    if (i === allItems.length - 1) {
      arr.push({ value: "", label: "All" });
    }
    return arr;
  }, []);

  const [exportLoading, setExportLoading] = useState(false);

  const handleExportTableData = async () => {
    if (exportLoading) return;
    setExportLoading(true);
    try {
      const response = await fetchQcApi();
      if (response?.canceled) {
        toast.info(response?.message);
        return;
      }
      if (!response?.success) {
        toast.error(response?.message || "Failed to fetch QC data for export.");
        return;
      }
      const rows = buildQcExportRows(response.result);
      if (!rows.length) {
        toast.warn("No data available to export for the current filters.");
        return;
      }
      downloadQcExportExcel(rows, buildQcExportFileName());
      toast.success("QC data exported successfully.");
    } catch (error) {
      toast.error("Failed to export QC data.");
    } finally {
      setExportLoading(false);
    }
  };

  // Create overdue status options
  // const overdueStatusOptions = convertStatusMapToArrayOptions(TEST_OVERDUE_FLAGS, ["Queue", "Retest"], true);
  // overdueStatusOptions.unshift({ value: "", label: "All" });
  return (
    <Space className="QCFilterBar">
      <div className="field-container">
        <label>Search</label>
        <Input.Search
          allowClear
          placeholder="Search by WO, Genista ID, Test"
          className="QCFilterSearch"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          onSearch={handleOnSearch}
        />
      </div>
      <div className="field-container">
        <label>Workorder Date Range</label>
        <RangePicker
          allowClear={false}
          className="QCFilterDate"
          format="MM/DD/YYYY"
          value={dateRange}
          onChange={handleDateChange}
        />
      </div>
      <div className="field-container">
        <label>Test Category</label>
        <Select
          allowClear
          showSearch
          className="QCFilterBarCategory"
          popupClassName="QCFilterBarCategory__Menu"
          options={testCategories}
          defaultValue={testCategories[0]?.value}
          value={selectedCategory}
          onSelect={(v) => setSelectedCategory(v)}
          onClear={() => setSelectedCategory("")}
          filterOption={(inputValue, option) => option.value?.toLowerCase().startsWith(inputValue?.toLowerCase())}
          listHeight={400}
        />
      </div>
      <div className="field-container">
        <label>Customer</label>
        <Select
          allowClear
          showSearch
          className="QCFilterBarCategory"
          popupClassName="QCFilterBarCategory__Menu"
          loading={customerLoading}
          options={customerList}
          defaultValue={customerList[0]?.value}
          value={selectedCustomer}
          onSelect={(v) => setSelectedCustomer(v)}
          onClear={() => setSelectedCustomer("")}
          filterOption={(inputValue, option) => option.label?.toLowerCase().includes(inputValue?.toLowerCase())}
          listHeight={400}
        />
      </div>
      <div className="field-container">
        <label>Assign To</label>
        <Select
          allowClear
          showSearch
          className="QCFilterBarCategory"
          popupClassName="QCFilterBarCategory__Menu"
          loading={assignToLoading}
          options={assignToList}
          defaultValue={assignToList[0]?.value}
          value={selectedAssignTo}
          onSelect={(v) => setSelectedAssignTo(v)}
          onClear={() => setSelectedAssignTo("")}
          filterOption={(inputValue, option) => option.label?.toLowerCase().includes(inputValue?.toLowerCase())}
          listHeight={400}
        />
      </div>
      <div className="field-container">
        <label>Manual QC Status</label>
        <Select
          allowClear
          className="QCFilterBarCategory"
          popupClassName="QCFilterBarCategory__Menu"
          options={manualQcOptions}
          defaultValue={manualQcDefaultValue}
          value={selectedManualQC}
          onSelect={(v) => setSelectedManualQC(v)}
          onClear={() => setSelectedManualQC(manualQcDefaultValue)}
          listHeight={400}
        />
      </div>
      {/* <div className="field-container">
        <label>Due Status</label>
        <Select
          allowClear
          className="QCFilterBarCategory"
          popupClassName="QCFilterBarCategory__Menu"
          options={overdueStatusOptions}
          value={selectedOverdueStatus}
          onSelect={(v) => setSelectedOverdueStatus(v)}
          onClear={() => setSelectedOverdueStatus("")}
          listHeight={400}
        />
      </div> */}
      <div className="field-container">
        <label>Retest</label>
        <Switch
          onChange={handleRetestOnChange}
          className="QCFilterBarCategory__Switch"
        />
      </div>
      <div className="field-container">
        {/* <label>Export Excel</label> */}
        <Button
          type="primary"
          onClick={handleExportTableData}
          className="QCFilterBar__ExportButton"
          loading={exportLoading}
          disabled={exportLoading}
        >
          Export Excel
        </Button>
      </div>
      {/* <div className="field-container">
        <label>AI QC Status</label>
        <Select
          allowClear
          className="QCFilterBarCategory"
          popupClassName="QCFilterBarCategory__Menu"
          options={AI_QC_OPTIONS}
          defaultValue={AI_QC_OPTIONS[0]?.value}
          value={selectedAIQC}
          onSelect={(v) => handleAIQCChange(v)}
          onClear={() => handleAIQCChange("")}
          listHeight={400}
        />
      </div>
      <div className="field-container">
        <label>Software QC Status</label>
        <Select
          allowClear
          className="QCFilterBarCategory"
          popupClassName="QCFilterBarCategory__Menu"
          options={SOFTWARE_QC_OPTIONS}
          defaultValue={SOFTWARE_QC_OPTIONS[0]?.value}
          value={selectedSoftwareQC}
          onSelect={(v) => handleSoftwareQCChange(v)}
          onClear={() => handleSoftwareQCChange("")}
          listHeight={400}
        />
      </div> */}
      {/* <div className="field-container">
        <label>QC Status</label>
        <Select
          allowClear
          className="QCFilterBarCategory"
          popupClassName="QCFilterBarCategory__Menu"
          options={ML_SOFTWARE_QC_OPTIONS}
          defaultValue={ML_SOFTWARE_QC_OPTIONS[0]?.value}
          value={selectedMLSoftQC}
          onSelect={(v) => handleMLSoftQCChange(v)}
          onClear={() => handleMLSoftQCChange("")}
          listHeight={400}
        />
      </div> */}
      {/* <div className="field-container">
        <label>QC Discrepancy</label>
        <Switch
          checked={selectedAISoftwareQCDiscrepancy}
          onChange={handleDiscrepancyChange}
          className="QCFilterBarCategory__Switch"
        />
      </div> */}
    </Space>
  );
};

export default QCFilterBar;
